import { motion } from 'framer-motion';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Grid,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import {
  // AttachMoney,
  // CompareArrows,
  // Payments,
  // Schedule,
  CheckCircle,
  TrendingUp,
  ChevronRight,
  // Cancel,
  // Info,
  // Stars,
  // Assignment,
  // CardMembership,
  LocalOffer,
  Timer,
  Warning,
  Assignment
} from '@mui/icons-material';
import VisibilityIcon from '@mui/icons-material/Visibility';

const renderFeatureValue = (value) => {
  if (value === 'Unlimited') {
    return <Typography sx={{ color: 'success.main', fontWeight: 600 }}>{value}</Typography>;
  } else if (value === 'unknown') {
    return <Typography sx={{ color: 'warning.main' }}>-</Typography>;
  } else {
    return <Typography>{value}</Typography>;
  }
};

const PricingAnalysisSection = ({ pricingAnalysis, isProUser = false, handleTrialClick, serviceName }) => {
  if (!pricingAnalysis || !pricingAnalysis.has_pricing_info) return null;
  const gradientColors = [
    'rgba(249, 250, 251, 0.8)', // gray
    'rgba(239, 246, 255, 0.8)', // blue
    'rgba(245, 243, 255, 0.8)', // purple
    'rgba(238, 242, 255, 0.8)' // indigo
  ];

  const priceRange = pricingAnalysis.price_range || {};
  const freePlan = pricingAnalysis.free_plan || {};
  const trialOffering = pricingAnalysis.trial_offering || {};
  const pricingPlans = pricingAnalysis.pricing_plans || [];
  const planComparison = pricingAnalysis.plan_comparison_analysis || {};
  const featuresComparison = pricingAnalysis.features_comparison || {};
  const pricingConclusion = pricingAnalysis.pricing_conclusion || {};
  const pricingWeaknesses = pricingAnalysis.pricing_weaknesses || [];

  return (
    <Box sx={{ mt: 0, mb: 8 }}>
      {/* Main Component Title */}
      <Stack my={4} spacing={2} direction="row" alignItems="center" justifyContent="center">
        <Typography variant="h3" align="center">
          Pricing Analysis
        </Typography>
      </Stack>

      {/* Overview Card */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Card
          sx={{
            mb: 4,
            position: 'relative',
            overflow: 'hidden',
            borderRadius: { xs: 2, md: 3 },
            boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
          }}
        >
          {/* Gradient overlay */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0,118,255,0.05) 0%, rgba(103,58,183,0.05) 50%, rgba(103,58,183,0) 100%)',
              zIndex: 0
            }}
          />

          <CardContent
            sx={{
              position: 'relative',
              zIndex: 1,
              p: { xs: 2, md: 3 }
            }}
          >
            <Stack spacing={3}>
              {/* Section Header */}
              <Box sx={{ mb: 0 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    background: 'linear-gradient(135deg, #0070f3 0%, #6743b5 60%, rgba(103,58,183,0.7) 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'primary.main'
                  }}
                >
                  Pricing Overview
                </Typography>
              </Box>

              {/* Content Stack */}
              <Stack spacing={3}>
                {/* Basic Information */}
                <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                  <Box
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      position: 'relative',
                      isolation: 'isolate',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        padding: '1px',
                        borderRadius: 'inherit',
                        background: 'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, ' + 'linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                        zIndex: -1
                      },
                      boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                      }
                    }}
                  >
                    <Stack spacing={2}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1.5
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5
                          }}
                        >
                          <Box sx={{ color: 'primary.main' }}>
                            <LocalOffer />
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Basic Information
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.secondary',
                              fontWeight: 500
                            }}
                          >
                            Price Range:
                          </Typography>
                          <Box
                            sx={{
                              px: 2,
                              py: 0.75,
                              borderRadius: 2,
                              background: 'linear-gradient(135deg, rgba(0,118,255,0.1) 0%, rgba(103,58,183,0.1) 100%)',
                              border: '1px solid',
                              borderColor: 'primary.main',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                background: 'linear-gradient(135deg, #0070f3 0%, #6743b5 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                color: 'transparent'
                              }}
                            >
                              {`${priceRange.lowest} - ${priceRange.highest} ${priceRange.currency || ''}`}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      {[
                        { label: 'Pricing Model', value: pricingAnalysis.pricing_model || 'Not specified' },
                        { label: 'Billing Options', value: pricingAnalysis.billing_options?.join(', ') || 'Not specified' }
                      ].map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mt: 0.5 }} />
                          <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                            <Box component="span" sx={{ fontWeight: 600 }}>
                              {item.label}:
                            </Box>{' '}
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </motion.div>

                {/* Plans & Trials */}
                <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                  <Box
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      position: 'relative',
                      isolation: 'isolate',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        padding: '1px',
                        borderRadius: 'inherit',
                        background: 'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, ' + 'linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                        zIndex: -1
                      },
                      boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                      }
                    }}
                  >
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ color: 'primary.main' }}>
                          <Timer />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Plans & Trials
                        </Typography>
                      </Box>
                      {freePlan.available && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mt: 0.5 }} />
                          <Box sx={{ position: 'relative', flex: 1 }}>
                            <Typography component="span" sx={{ fontWeight: 600 }}>
                              Free Plan:{' '}
                            </Typography>
                            {isProUser ? (
                              <Typography component="span">{freePlan.details || 'Details not available'}</Typography>
                            ) : (
                              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                <Box
                                  sx={{
                                    filter: 'blur(5px)',
                                    userSelect: 'none',
                                    opacity: 0.6
                                  }}
                                >
                                  {freePlan.details || 'Details not available'}
                                </Box>
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    opacity: 0.5,
                                    cursor: 'pointer',
                                    '&:hover': {
                                      opacity: 1
                                    }
                                  }}
                                  onClick={handleTrialClick}
                                >
                                  <VisibilityIcon fontSize="small" />
                                  <Typography variant="caption" color="black" ml={1}>
                                    View
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}
                      {trialOffering.available && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mt: 0.5 }} />
                          <Box sx={{ position: 'relative', flex: 1 }}>
                            <Typography component="span" sx={{ fontWeight: 600 }}>
                              Trial Offering:{' '}
                            </Typography>
                            {isProUser ? (
                              <Typography component="span">{trialOffering.details || 'Details not available'}</Typography>
                            ) : (
                              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                <Box
                                  sx={{
                                    filter: 'blur(5px)',
                                    userSelect: 'none',
                                    opacity: 0.6
                                  }}
                                >
                                  {trialOffering.details || 'Details not available'}
                                </Box>
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    opacity: 0.5,
                                    cursor: 'pointer',
                                    '&:hover': {
                                      opacity: 1
                                    }
                                  }}
                                  onClick={handleTrialClick}
                                >
                                  <VisibilityIcon fontSize="small" />
                                  <Typography variant="caption" color="black" ml={1}>
                                    View
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </motion.div>
                {/* Analysis Cards - Updated design */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    {/* Pricing Strategy - Moved to top */}
                    <Grid item xs={12}>
                      <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                        <Box
                          sx={{
                            p: 3,
                            height: '100%',
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            position: 'relative',
                            isolation: 'isolate',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              inset: 0,
                              padding: '1px',
                              borderRadius: 'inherit',
                              background:
                                'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                              WebkitMask: 'linear-gradient(#fff 0 0) content-box, ' + 'linear-gradient(#fff 0 0)',
                              WebkitMaskComposite: 'xor',
                              maskComposite: 'exclude',
                              zIndex: -1
                            },
                            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                            }
                          }}
                        >
                          <Stack spacing={2}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5
                              }}
                            >
                              <Box
                                sx={{
                                  color: 'primary.main',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <TrendingUp />
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Pricing Strategy
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1
                              }}
                            >
                              <ChevronRight
                                sx={{
                                  color: 'primary.main',
                                  fontSize: 20,
                                  mt: 0.5
                                }}
                              />
                              <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                {isProUser ? (
                                  pricingAnalysis.pricing_strategy
                                ) : (
                                  <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                                    {/* Blurred content */}
                                    <Box
                                      sx={{
                                        filter: 'blur(5px)',
                                        userSelect: 'none',
                                        position: 'relative',
                                        padding: 0,
                                        opacity: 0.6
                                      }}
                                    >
                                      {pricingAnalysis.pricing_strategy}
                                    </Box>

                                    {/* Overlay with view button */}
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        opacity: 0.5,
                                        transition: 'opacity 0.3s, transform 0.3s',
                                        cursor: 'pointer',
                                        '&:hover': {
                                          opacity: 1,
                                          transform: 'scale(1.05)'
                                        }
                                      }}
                                      onClick={handleTrialClick}
                                    >
                                      <VisibilityIcon />
                                      <Typography variant="caption" color="black" ml={1}>
                                        View
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      </motion.div>
                    </Grid>
                  </Grid>
                </motion.div>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </motion.div>

      {/* Pricing Plans */}
      {pricingPlans.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 3,
              background: 'linear-gradient(135deg, #0070f3 0%, #6743b5 60%, rgba(103,58,183,0.7) 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'primary.main'
            }}
          >
            Plans: {serviceName}
          </Typography>

          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
            {pricingPlans.map((plan, index) => (
              <Grid item xs={12} md={6} key={index}>
                <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                  {isProUser || index === 0 ? (
                    <Box
                      sx={{
                        p: { xs: 2, md: 4 },
                        height: '100%',
                        borderRadius: 4,
                        bgcolor: gradientColors[index],
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                        }
                      }}
                    >
                      <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
                        {plan.plan_name}
                      </Typography>
                      <Typography variant="h3" sx={{ mb: 3, fontWeight: 800 }}>
                        {plan.price}
                        <Typography component="span" variant="body1" sx={{ ml: 1, color: 'text.secondary' }}>
                          /{plan.billing_frequency}
                        </Typography>
                      </Typography>
                      <Stack spacing={2}>
                        {plan.key_features.map((feature, idx) => (
                          <Stack direction="row" spacing={1} alignItems="flex-start" key={idx}>
                            <CheckCircle sx={{ color: 'success.main', fontSize: 20, mt: 0.3 }} />
                            <Typography variant="body2" color="text.secondary">
                              {feature}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  ) : (
                    <Box sx={{ position: 'relative' }}>
                      {/* Blurred content */}
                      <Box
                        sx={{
                          filter: 'blur(5px)',
                          userSelect: 'none',
                          opacity: 0.6,
                          p: 4,
                          height: '100%',
                          borderRadius: 4,
                          bgcolor: gradientColors[index]
                        }}
                      >
                        <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
                          {plan.plan_name}
                        </Typography>
                        <Typography variant="h3" sx={{ mb: 3, fontWeight: 800 }}>
                          {plan.price}
                          <Typography component="span" variant="body1" sx={{ ml: 1, color: 'text.secondary' }}>
                            /{plan.billing_frequency}
                          </Typography>
                        </Typography>
                        <Stack spacing={2}>
                          {plan.key_features.map((feature, idx) => (
                            <Stack direction="row" spacing={1} alignItems="flex-start" key={idx}>
                              <CheckCircle sx={{ color: 'success.main', fontSize: 20, mt: 0.3 }} />
                              <Typography variant="body2" color="text.secondary">
                                {feature}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                      {/* View button overlay */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          opacity: 0.5,
                          cursor: 'pointer',
                          borderRadius: 4,
                          '&:hover': {
                            opacity: 1
                          }
                        }}
                        onClick={handleTrialClick}
                      >
                        <VisibilityIcon fontSize="small" />
                        <Typography variant="caption" color="black" ml={1}>
                          View
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      )}

      {/* Plan Analysis */}
      {planComparison.plans?.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.6 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 3,
              background: 'linear-gradient(135deg, #0070f3 0%, #6743b5 60%, rgba(103,58,183,0.7) 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'primary.main'
            }}
          >
            Plans Analysis
          </Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {planComparison.plans.map((plan, index) => (
              <Grid item xs={12} md={6} key={index}>
                <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                  <Box
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 4,
                      bgcolor: gradientColors[index],
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                      {plan.plan_name}
                    </Typography>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Use Cases
                        </Typography>
                        {isProUser ? (
                          <Typography variant="body2">{plan.use_cases}</Typography>
                        ) : (
                          <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                            <Box
                              sx={{
                                filter: 'blur(5px)',
                                userSelect: 'none',
                                opacity: 0.6
                              }}
                            >
                              {plan.use_cases}
                            </Box>
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                opacity: 0.5,
                                cursor: 'pointer',
                                '&:hover': {
                                  opacity: 1
                                }
                              }}
                              onClick={handleTrialClick}
                            >
                              <VisibilityIcon fontSize="small" />
                              <Typography variant="caption" color="black" ml={1}>
                                View
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Target Users
                        </Typography>
                        {isProUser ? (
                          <Typography variant="body2">{plan.target_users}</Typography>
                        ) : (
                          <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                            <Box
                              sx={{
                                filter: 'blur(5px)',
                                userSelect: 'none',
                                opacity: 0.6
                              }}
                            >
                              {plan.target_users}
                            </Box>
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                opacity: 0.5,
                                cursor: 'pointer',
                                '&:hover': {
                                  opacity: 1
                                }
                              }}
                              onClick={handleTrialClick}
                            >
                              <VisibilityIcon fontSize="small" />
                              <Typography variant="caption" color="black" ml={1}>
                                View
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Value Proposition
                        </Typography>
                        {isProUser ? (
                          <Typography variant="body2">{plan.value_proposition}</Typography>
                        ) : (
                          <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                            <Box
                              sx={{
                                filter: 'blur(5px)',
                                userSelect: 'none',
                                opacity: 0.6
                              }}
                            >
                              {plan.value_proposition}
                            </Box>
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                opacity: 0.5,
                                cursor: 'pointer',
                                '&:hover': {
                                  opacity: 1
                                }
                              }}
                              onClick={handleTrialClick}
                            >
                              <VisibilityIcon fontSize="small" />
                              <Typography variant="caption" color="black" ml={1}>
                                View
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Stack>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      )}

      {/* Features Comparison */}
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          maxWidth: '100%',
          display: 'block' // Force block display
        }}
      >
        <Box
          sx={{
            minWidth: { xs: '100%', sm: '100%', md: '800px' }, // Make it full width on mobile
            width: '100%'
          }}
        >
          <Table sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Feature</TableCell>
                {pricingPlans.map((plan, index) => (
                  <TableCell key={index} align="center" sx={{ fontWeight: 700 }}>
                    {plan.plan_name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(featuresComparison).map(([feature, values], index) => (
                <TableRow
                  key={index}
                  sx={{
                    '&:nth-of-type(odd)': { bgcolor: 'background.default' }
                  }}
                >
                  <TableCell>
                    {isProUser || index === 0 ? (
                      <Typography>{feature}</Typography>
                    ) : (
                      <Box sx={{ position: 'relative' }}>
                        <Box
                          sx={{
                            filter: 'blur(5px)',
                            userSelect: 'none',
                            opacity: 0.6
                          }}
                        >
                          {feature}
                        </Box>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            opacity: 0.5,
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 1
                            }
                          }}
                          onClick={handleTrialClick}
                        >
                          <VisibilityIcon fontSize="small" />
                          <Typography variant="caption" color="black" ml={1}>
                            View
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </TableCell>
                  {values.map((value, idx) => (
                    <TableCell key={idx} align="center">
                      {isProUser || index === 0 ? (
                        renderFeatureValue(value)
                      ) : (
                        <Box sx={{ position: 'relative' }}>
                          <Box
                            sx={{
                              filter: 'blur(5px)',
                              userSelect: 'none',
                              opacity: 0.6
                            }}
                          >
                            {renderFeatureValue(value)}
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              opacity: 0.5,
                              cursor: 'pointer',
                              '&:hover': {
                                opacity: 1
                              }
                            }}
                            onClick={handleTrialClick}
                          >
                            <VisibilityIcon fontSize="small" />
                            <Typography variant="caption" color="black" ml={1}>
                              View
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {/* Conclusion & Recommendations */}
      {(pricingConclusion.summary || pricingWeaknesses.length > 0) && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.8 }}>
          <Card
            sx={{
              mb: 4,
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
            }}
          >
            {/* Gradient overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(0,118,255,0.05) 0%, rgba(103,58,183,0.05) 50%, rgba(103,58,183,0) 100%)',
                zIndex: 0
              }}
            />

            <CardContent sx={{ position: 'relative', zIndex: 1 }}>
              <Stack spacing={3}>
                {/* Section Header */}
                <Box sx={{ mb: 0 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      background: 'linear-gradient(135deg, #0070f3 0%, #6743b5 60%, rgba(103,58,183,0.7) 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      color: 'primary.main'
                    }}
                  >
                    Evaluation and Recommendations
                  </Typography>
                </Box>

                {/* Content Stack */}
                <Stack spacing={3}>
                  {/* Conclusion */}
                  <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                    <Box
                      sx={{
                        p: 3,
                        height: '100%',
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        position: 'relative',
                        isolation: 'isolate',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          inset: 0,
                          padding: '1px',
                          borderRadius: 'inherit',
                          background:
                            'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                          WebkitMask: 'linear-gradient(#fff 0 0) content-box, ' + 'linear-gradient(#fff 0 0)',
                          WebkitMaskComposite: 'xor',
                          maskComposite: 'exclude',
                          zIndex: -1
                        },
                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                        }
                      }}
                    >
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5
                          }}
                        >
                          <Box sx={{ color: 'primary.main' }}>
                            <Assignment />
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Conclusion
                          </Typography>
                        </Box>
                        {isProUser ? (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mt: 0.3 }} />
                            <Typography variant="body1">{pricingConclusion.summary}</Typography>
                          </Box>
                        ) : (
                          <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mt: 0.3 }} />
                              <Box
                                sx={{
                                  filter: 'blur(5px)',
                                  userSelect: 'none',
                                  opacity: 0.6
                                }}
                              >
                                {pricingConclusion.summary}
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                opacity: 0.5,
                                cursor: 'pointer',
                                '&:hover': {
                                  opacity: 1
                                }
                              }}
                              onClick={handleTrialClick}
                            >
                              <VisibilityIcon fontSize="small" />
                              <Typography variant="caption" color="black" ml={1}>
                                View
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            Recommendations:
                          </Typography>
                          <Stack spacing={1}>
                            {pricingConclusion.recommendations?.length > 0 &&
                              pricingConclusion.recommendations.map((rec, index) => (
                                <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                                  <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mt: 0.3 }} />
                                  {isProUser ? (
                                    <Typography variant="body2">{rec}</Typography>
                                  ) : (
                                    <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                                      <Box
                                        sx={{
                                          filter: 'blur(5px)',
                                          userSelect: 'none',
                                          opacity: 0.6
                                        }}
                                      >
                                        {rec}
                                      </Box>
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          top: 0,
                                          left: 0,
                                          right: 0,
                                          bottom: 0,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                          opacity: 0.5,
                                          cursor: 'pointer',
                                          '&:hover': {
                                            opacity: 1
                                          }
                                        }}
                                        onClick={handleTrialClick}
                                      >
                                        <VisibilityIcon fontSize="small" />
                                        <Typography variant="caption" color="black" ml={1}>
                                          View
                                        </Typography>
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              ))}
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>
                  </motion.div>

                  {/* Areas for Improvement */}
                  <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                    <Box
                      sx={{
                        p: 3,
                        height: '100%',
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        position: 'relative',
                        isolation: 'isolate',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          inset: 0,
                          padding: '1px',
                          borderRadius: 'inherit',
                          background: 'linear-gradient(135deg, rgba(255,87,34,0.5) 0%, rgba(255,152,0,0.5) 60%, rgba(255,152,0,0.2) 100%)',
                          WebkitMask: 'linear-gradient(#fff 0 0) content-box, ' + 'linear-gradient(#fff 0 0)',
                          WebkitMaskComposite: 'xor',
                          maskComposite: 'exclude',
                          zIndex: -1
                        },
                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                        }
                      }}
                    >
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5
                          }}
                        >
                          <Box sx={{ color: 'warning.main' }}>
                            <Warning />
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Areas for Improvement
                          </Typography>
                        </Box>
                        <Stack spacing={1}>
                          {pricingWeaknesses.length > 0 &&
                            pricingWeaknesses.map((weakness, index) => (
                              <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                                <ChevronRight sx={{ color: 'warning.main', fontSize: 20, mt: 0.3 }} />
                                {isProUser ? (
                                  <Typography variant="body2">{weakness}</Typography>
                                ) : (
                                  <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                                    <Box
                                      sx={{
                                        filter: 'blur(5px)',
                                        userSelect: 'none',
                                        opacity: 0.6
                                      }}
                                    >
                                      {weakness}
                                    </Box>
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        opacity: 0.5,
                                        cursor: 'pointer',
                                        '&:hover': {
                                          opacity: 1
                                        }
                                      }}
                                      onClick={handleTrialClick}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                      <Typography variant="caption" color="black" ml={1}>
                                        View
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            ))}
                        </Stack>
                      </Stack>
                    </Box>
                  </motion.div>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {!isProUser && (
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 4,
            backdropFilter: 'blur(5px)',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            textAlign: 'center',
            mt: 2
          }}
        >
          <Typography variant="h5" color="primary" align="center" mb={1}>
            Start your FREE 7 days trial to get access to all Pricing Strategy content!
          </Typography>
          <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={handleTrialClick}>
            Unlock Pricing Strategy
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default PricingAnalysisSection;
